import { Service } from '@/api/apiServices';
import { ConfirmDialog } from '@/components/feature/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface ServiceDataTableRowActionsProps {
  service: Service;
  table: Table<Service>;
  onEdit: (service: Service) => void;
  onDelete: (id: number) => void;
  onSwapPosition: (currentRow: Service, targetRow: Service, direction: 'up' | 'down') => void;
}

export function ServiceDataTableRowActions({
  service,
  table,
  onEdit,
  onDelete,
  onSwapPosition,
}: ServiceDataTableRowActionsProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Get all rows from the table, not just the current page
  const allRows = table.getCoreRowModel().rows;
  const rowIndex = allRows.findIndex(row => row.original.id === service.id);
  const isFirstRow = rowIndex === 0;
  const isLastRow = rowIndex === allRows.length - 1;

  const handleMoveUp = () => {
    if (!isFirstRow) {
      const aboveRow = allRows[rowIndex - 1].original;
      onSwapPosition(service, aboveRow, 'up');
    }
  };

  const handleMoveDown = () => {
    if (!isLastRow) {
      const belowRow = allRows[rowIndex + 1].original;
      onSwapPosition(service, belowRow, 'down');
    }
  };

  const handleDelete = () => {
    onDelete(service.id);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[160px]">
          <DropdownMenuItem onClick={() => onEdit(service)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowDeleteConfirm(true)} className="text-destructive">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
          {!isFirstRow && (
            <DropdownMenuItem onClick={handleMoveUp}>
              <ArrowUp className="mr-2 h-4 w-4" />
              Move Up
            </DropdownMenuItem>
          )}
          {!isLastRow && (
            <DropdownMenuItem onClick={handleMoveDown}>
              <ArrowDown className="mr-2 h-4 w-4" />
              Move Down
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Service"
        description={`Are you sure you want to delete "${service.serTitle}" ? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleDelete}
      />
    </>
  );
}
